import {
  PropsWithChildren,
  useEffect,
  useState,
  type FunctionComponent,
} from 'react';
import {
  CognitoAuthContext,
  CognitoActionContext,
  AuthenticationStateParams,
  SessionEndOptions,
} from './';
import { CaireUserTokenClaims } from '@/lib/auth/jwt';

export interface CognitoMockProviderProps extends PropsWithChildren {
  loading?: boolean;
  authenticated?: boolean;
  token?: string | null;
  idpName?: string | null;
  user?: Partial<CaireUserTokenClaims> | null;
  signOutSpy?: (options?: SessionEndOptions) => void;
  refetchMetadataSpy?: () => void;
  redirectToLoginSpy?: (state?: AuthenticationStateParams) => void;
  redirectAfterLoginSpy?: () => void;
  possiblyRefreshAccessTokenSpy?: () => void;
}

const notImplementedStub = () => {
  throw new Error('Not implemented in CognitoMockProvider test environment.');
};

const DEFAULT_TEST_USER: CaireUserTokenClaims = {
  authId: '054d9008-8a3c-4c00-925b-41501985c084',
  email: 'test@example.com',
  emailVerified: true,
  isSSOAuthUser: false,
  programs: ['6f3fd233-53f4-3e1d-b38e-3083917b67d8'],
  staffId: '8a94908b-0953-4886-9e9d-5c6d418bd187',
  expires: new Date(Date.now() + 10000),
};

export const CognitoMockProvider: FunctionComponent<
  CognitoMockProviderProps
> = ({
  loading = false,
  authenticated = !loading,
  token = 'test-cognito-token',
  idpName = null,
  user = {},
  signOutSpy,
  refetchMetadataSpy,
  redirectToLoginSpy,
  redirectAfterLoginSpy,
  possiblyRefreshAccessTokenSpy,
  children,
}) => {
  const [error, setError] = useState<Error | null>(null);
  const [authToken, setAuthToken] = useState(authenticated ? token : null);
  const [authUser, setAuthUser] = useState(
    authenticated
      ? {
          ...DEFAULT_TEST_USER,
          ...user,
        }
      : null
  );

  // Reset fixture state if authenticated state is toggled in re-render
  useEffect(() => {
    if (authenticated) {
      setAuthUser({
        ...DEFAULT_TEST_USER,
        ...user,
      });
      setAuthToken(token);
    } else {
      setAuthToken(null);
      setAuthUser(null);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps
     -- trigger mock state reset only when authenticated state changes */
  }, [authenticated]);

  const clearAuthState = () => {
    setAuthToken(null);
    setAuthUser(null);
  };

  return (
    <CognitoAuthContext.Provider
      value={{
        loading: loading && !authenticated,
        refreshing: loading,
        error: error,
        token: authToken,
        idpName,
        authUser,
        setError,
        clearAuthState,
        // Functions for manipulating the auth state are not implemented in this mock, as they
        // the scope of this mock is to provide a static snapshot auth state for testing.
        setToken: notImplementedStub,
        setRefreshing: notImplementedStub,
        setIdpName: notImplementedStub,
      }}>
      <CognitoActionContext.Provider
        value={{
          endSession: (...args) =>
            Promise.resolve(signOutSpy?.(...args) ?? undefined),
          refetchUserMetadata: () =>
            Promise.resolve(refetchMetadataSpy?.() ?? undefined),
          redirectToLogin: () =>
            Promise.resolve(redirectToLoginSpy?.() ?? undefined),
          redirectAfterLogin: () => redirectAfterLoginSpy?.(),
          possiblyRefreshAccessToken: () =>
            Promise.resolve(possiblyRefreshAccessTokenSpy?.() ?? undefined),
        }}>
        {children}
      </CognitoActionContext.Provider>
    </CognitoAuthContext.Provider>
  );
};
